import { useMutation, useQueryClient } from "react-query";
import { RestError } from "../../services/rest-service/RestService";
import { IUICustomizationPresetDescriptionDTO, IUICustomizationPresetDTO } from "../../services/ui-customization-data-service/Models";
import UICustomizationDataService from "../../services/ui-customization-data-service/UICustomizationDataService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";
import { MutationContextType } from "./Common";
import { keys } from "./Keys";
import { keys as userKeys } from "../user-role-administration/Keys";

export type UseSaveUICustomizationPresetParameters = {
    groupID: number;
    organizationID: number;
} & BaseUseMutationParameters<IUICustomizationPresetDTO, IUICustomizationPresetDTO>;

export function useSaveUICustomizationPreset({
    groupID,
    organizationID,
    onError,
    onSuccess
}: UseSaveUICustomizationPresetParameters) {
    const queryClient = useQueryClient();

    return useMutation<IUICustomizationPresetDTO, RestError, IUICustomizationPresetDTO, MutationContextType>(
        (preset) => {
            return UICustomizationDataService.saveUICustomizationPreset(
                groupID,
                organizationID,
                preset
            );
        },
        {
            onMutate: async (preset: IUICustomizationPresetDTO) => {

                await queryClient.cancelQueries(keys.all);

                const previousPresets = queryClient.getQueryData<Array<IUICustomizationPresetDescriptionDTO>>(
                    keys.presetList(
                        organizationID
                    )
                );
                queryClient.setQueryData<Array<IUICustomizationPresetDescriptionDTO>>(

                    keys.presetList(
                        organizationID
                    ),
                    (old: Array<IUICustomizationPresetDescriptionDTO> | undefined) => {

                        const presets = old ?? [];

                        const existingPreset = presets.find(p => p.id === preset.id);

                        const newModel: IUICustomizationPresetDescriptionDTO = {
                            id: preset.id,
                            name: preset.name,
                            description: preset.description
                        };

                        if (old) {
                            const copy: Array<IUICustomizationPresetDescriptionDTO> = existingPreset
                                ? presets.map(p => p.id === preset.id ? newModel : p)
                                : [...presets, newModel];

                            return copy;
                        } else {
                            return [newModel];
                        };
                    }
                );

                return {
                    previousPresets: previousPresets
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_, error, preset, context) => {

                if (error && context?.previousPresets) {
                    queryClient.setQueryData<Array<IUICustomizationPresetDescriptionDTO>>(
                        keys.presetList(
                            organizationID
                        ),
                        context?.previousPresets
                    );
                }

                if (!error) {
                    queryClient.invalidateQueries(keys.settings());
                    queryClient.invalidateQueries(keys.effectiveSettings());
                    queryClient.invalidateQueries(keys.presetList(organizationID));
                    queryClient.invalidateQueries(keys.preset(preset.id));
                    queryClient.invalidateQueries(keys.presetsUserLists());
                    queryClient.invalidateQueries(userKeys.userLists());
                }
            }
        }
    );
}
